
import { supabase } from '@/integrations/supabase/client';
import { Poll } from '@/types/poll';
import { PointsService } from '@/services/points/PointsService';
import { debugVoteFlow } from '@/utils/debug';

export interface VoteSubmissionResult {
  success: boolean;
  error?: string;
  pointsAwarded?: number;
  voteId?: string;
  selectedOptionId?: number;
}

export interface VoteValidationResult {
  canVote: boolean;
  reason?: string;
  existingVoteId?: string;
  selectedOptionId?: number;
  voteType?: string;
}

interface ValidationRpcResult {
  valid: boolean;
  error?: string;
  message?: string;
}

interface CanVoteRpcResult {
  can_vote: boolean;
  reason?: string;
  vote_type?: string;
}

interface SubmissionRpcResult {
  success: boolean;
  error?: string;
  vote_id?: string;
  vote_type?: string;
}

export class VotingService {
  /**
   * Enhanced validation with public voting support
   */
  static async validateVote(
    pollId: string, 
    userId?: string, 
    anonymousId?: string
  ): Promise<VoteValidationResult> {
    try {
      debugVoteFlow('VOTING_SERVICE_VALIDATE', { 
        pollId, 
        userId: userId ? 'present' : 'none', 
        anonymousId: anonymousId ? 'present' : 'none' 
      });

      // Check for existing vote first to get selected option
      const existingVote = await this.getUserVote(pollId, userId, anonymousId);
      if (existingVote !== null) {
        return {
          canVote: false,
          reason: 'User has already voted on this poll',
          selectedOptionId: existingVote
        };
      }

      // Use the new can_user_vote_on_poll function for authenticated users
      if (userId) {
        const { data: canVoteResult, error: canVoteError } = await supabase
          .rpc('can_user_vote_on_poll', {
            poll_id_param: pollId,
            user_id_param: userId
          });

        if (canVoteError) {
          debugVoteFlow('VOTING_SERVICE_CAN_VOTE_RPC_ERROR', { error: canVoteError });
          return await this.fallbackValidation(pollId, userId, anonymousId);
        }

        const result = canVoteResult as unknown as CanVoteRpcResult;
        if (!result?.can_vote) {
          debugVoteFlow('VOTING_SERVICE_CAN_VOTE_FAILED', { 
            reason: result?.reason 
          });
          return { 
            canVote: false, 
            reason: result?.reason || 'Cannot vote on this poll' 
          };
        }

        debugVoteFlow('VOTING_SERVICE_CAN_VOTE_SUCCESS', { 
          pollId, 
          voteType: result.vote_type 
        });
        return { 
          canVote: true, 
          voteType: result.vote_type 
        };
      }

      // For anonymous users, use the existing validation
      const { data: validationResult, error: validationError } = await supabase
        .rpc('validate_vote_submission', {
          poll_id_param: pollId,
          user_id_param: null,
          anonymous_id_param: anonymousId || null
        });

      if (validationError) {
        debugVoteFlow('VOTING_SERVICE_VALIDATION_RPC_ERROR', { error: validationError });
        return await this.fallbackValidation(pollId, userId, anonymousId);
      }

      const result = validationResult as unknown as ValidationRpcResult;
      if (!result?.valid) {
        debugVoteFlow('VOTING_SERVICE_VALIDATION_FAILED', { 
          error: result?.error,
          message: result?.message 
        });
        return { 
          canVote: false, 
          reason: result?.message || 'Validation failed' 
        };
      }

      debugVoteFlow('VOTING_SERVICE_VALIDATION_SUCCESS', { pollId });
      return { canVote: true };

    } catch (error) {
      debugVoteFlow('VOTING_SERVICE_VALIDATION_EXCEPTION', { error });
      return await this.fallbackValidation(pollId, userId, anonymousId);
    }
  }

  /**
   * Enhanced fallback validation with public voting support
   */
  private static async fallbackValidation(
    pollId: string,
    userId?: string,
    anonymousId?: string
  ): Promise<VoteValidationResult> {
    try {
      debugVoteFlow('VOTING_SERVICE_FALLBACK_VALIDATION', { pollId });

      // Check if poll exists and is active
      const { data: poll, error: pollError } = await supabase
        .from('polls')
        .select('status, closes_at, anonymous_allowed, public_voting_allowed')
        .eq('id', pollId)
        .single();

      if (pollError) {
        debugVoteFlow('VOTING_SERVICE_FALLBACK_POLL_ERROR', { error: pollError });
        return { canVote: false, reason: 'Poll not found' };
      }

      if (poll.status !== 'live') {
        return { canVote: false, reason: 'Poll is not active' };
      }

      if (poll.closes_at && new Date(poll.closes_at) < new Date()) {
        return { canVote: false, reason: 'Poll has closed' };
      }

      // Check for existing votes and get selected option
      if (userId) {
        // Check user permissions based on role
        const { data: profile } = await supabase
          .from('profiles')
          .select('role, verified')
          .eq('id', userId)
          .single();

        if (profile?.role === 'public' && !poll.public_voting_allowed) {
          return { 
            canVote: false, 
            reason: 'Public voting not allowed for this poll' 
          };
        }

        if (profile?.role === 'pollster' && !profile.verified) {
          return { 
            canVote: false, 
            reason: 'Pollster not verified' 
          };
        }

        try {
          const { data: existingVote } = await supabase
            .from('poll_votes')
            .select('id, option_id')
            .eq('poll_id', pollId)
            .eq('user_id', userId)
            .maybeSingle();

          if (existingVote) {
            return { 
              canVote: false, 
              reason: 'User has already voted', 
              existingVoteId: existingVote.id,
              selectedOptionId: existingVote.option_id
            };
          }
        } catch (error) {
          debugVoteFlow('VOTING_SERVICE_FALLBACK_VOTE_CHECK_EXCEPTION', { error });
        }
      }

      if (anonymousId) {
        if (!poll.anonymous_allowed) {
          return { canVote: false, reason: 'Anonymous voting not allowed for this poll' };
        }

        try {
          const { data: existingAnonymousVote } = await supabase
            .from('anonymous_poll_votes')
            .select('id, option_id')
            .eq('poll_id', pollId)
            .eq('anonymous_id', anonymousId)
            .maybeSingle();

          if (existingAnonymousVote) {
            return { 
              canVote: false, 
              reason: 'Anonymous user has already voted', 
              existingVoteId: existingAnonymousVote.id,
              selectedOptionId: existingAnonymousVote.option_id
            };
          }
        } catch (error) {
          debugVoteFlow('VOTING_SERVICE_FALLBACK_ANONYMOUS_CHECK_EXCEPTION', { error });
        }
      }

      return { canVote: true };
    } catch (error) {
      debugVoteFlow('VOTING_SERVICE_FALLBACK_VALIDATION_ERROR', { error });
      return { canVote: false, reason: 'System error during validation' };
    }
  }

  /**
   * Simplified vote submission - database-first approach with improved anonymous handling
   */
  static async submitVote(
    pollId: string,
    optionId: number,
    userId?: string,
    anonymousId?: string,
    poll?: Poll
  ): Promise<VoteSubmissionResult> {
    debugVoteFlow('VOTING_SERVICE_SUBMIT_START', { 
      pollId, 
      optionId, 
      userId: userId ? 'present' : 'none', 
      anonymousId: anonymousId ? 'present' : 'none' 
    });

    try {
      // For anonymous users, use direct RPC approach since submit_vote_safely might not handle anonymous properly
      if (anonymousId && !userId) {
        return await this.submitAnonymousVote(pollId, optionId, anonymousId, poll);
      }

      // For authenticated users, try the enhanced submission function
      const { data: submissionResult, error: submissionError } = await supabase
        .rpc('submit_vote_safely', {
          poll_id_param: pollId,
          option_id_param: optionId,
          user_id_param: userId || null,
          anonymous_id_param: anonymousId || null
        });

      if (submissionError) {
        debugVoteFlow('VOTING_SERVICE_SUBMIT_RPC_ERROR', { error: submissionError });
        return await this.fallbackSubmission(pollId, optionId, userId, anonymousId, poll);
      }

      const result = submissionResult as unknown as SubmissionRpcResult;
      if (!result?.success) {
        debugVoteFlow('VOTING_SERVICE_SUBMIT_FAILED', { 
          error: result?.error,
          result: result 
        });
        return { 
          success: false, 
          error: result?.error || 'Vote submission failed' 
        };
      }

      debugVoteFlow('VOTING_SERVICE_SUBMIT_SUCCESS', { 
        voteId: result.vote_id,
        voteType: result.vote_type 
      });

      // Handle points for authenticated users
      let pointsAwarded = 0;
      if (userId && result.vote_type === 'authenticated') {
        try {
          debugVoteFlow('VOTING_SERVICE_POINTS_START', { userId, pollId });
          
          let countryCode = poll?.country_code;
          if (!countryCode) {
            countryCode = await PointsService.getPollCountryCode(pollId);
          }

          if (countryCode) {
            const pointsEnabled = await PointsService.isPollPointsEnabled(pollId);
            
            if (pointsEnabled) {
              const pointsResult = await PointsService.awardPollPoints(userId, pollId, countryCode);
              if (pointsResult.success && pointsResult.points) {
                pointsAwarded = pointsResult.points;
                debugVoteFlow('VOTING_SERVICE_POINTS_AWARDED', { points: pointsAwarded });
              }
            }
          }
        } catch (pointsError) {
          debugVoteFlow('VOTING_SERVICE_POINTS_EXCEPTION', { error: pointsError });
          console.warn('Points processing failed but vote recorded:', pointsError);
        }
      }

      return { 
        success: true, 
        voteId: result.vote_id,
        pointsAwarded: pointsAwarded > 0 ? pointsAwarded : undefined,
        selectedOptionId: optionId // Track the selected option
      };

    } catch (error) {
      debugVoteFlow('VOTING_SERVICE_SUBMIT_EXCEPTION', { error });
      return await this.fallbackSubmission(pollId, optionId, userId, anonymousId, poll);
    }
  }

  /**
   * Dedicated anonymous vote submission with immediate UI feedback coordination
   */
  private static async submitAnonymousVote(
    pollId: string,
    optionId: number,
    anonymousId: string,
    poll?: Poll
  ): Promise<VoteSubmissionResult> {
    try {
      debugVoteFlow('VOTING_SERVICE_ANONYMOUS_SUBMIT', { pollId, optionId, anonymousId });

      // Validate anonymous voting is allowed
      const validation = await this.validateVote(pollId, undefined, anonymousId);
      if (!validation.canVote) {
        return { success: false, error: validation.reason || 'Cannot vote on this poll' };
      }

      // Use the insert_anonymous_vote RPC function
      const { error } = await supabase.rpc('insert_anonymous_vote', {
        poll_id: pollId,
        option_id: optionId,
        anonymous_id: anonymousId
      });

      if (error) {
        debugVoteFlow('VOTING_SERVICE_ANONYMOUS_ERROR', { error });
        if (error.code === '23505') {
          return { success: false, error: 'You have already voted on this poll' };
        }
        return { success: false, error: 'Failed to record anonymous vote' };
      }

      debugVoteFlow('VOTING_SERVICE_ANONYMOUS_SUCCESS', { pollId, optionId });

      // Update poll statistics - this should trigger real-time updates
      try {
        await supabase.rpc('update_poll_options_counts', { poll_id: pollId });
        debugVoteFlow('VOTING_SERVICE_POLL_COUNTS_UPDATED', { pollId });
      } catch (updateError) {
        debugVoteFlow('VOTING_SERVICE_POLL_COUNTS_UPDATE_ERROR', { error: updateError });
        console.warn('Failed to update poll counts:', updateError);
      }

      return { 
        success: true,
        voteId: `anonymous-${anonymousId}-${pollId}-${optionId}`, // Generate a pseudo ID for tracking
        selectedOptionId: optionId // Track the selected option
      };

    } catch (error) {
      debugVoteFlow('VOTING_SERVICE_ANONYMOUS_EXCEPTION', { error });
      return { success: false, error: 'System error during anonymous vote submission' };
    }
  }

  /**
   * Enhanced fallback submission with better error handling
   */
  private static async fallbackSubmission(
    pollId: string,
    optionId: number,
    userId?: string,
    anonymousId?: string,
    poll?: Poll
  ): Promise<VoteSubmissionResult> {
    try {
      debugVoteFlow('VOTING_SERVICE_FALLBACK_SUBMISSION', { pollId, optionId });

      // Validate first
      const validation = await this.validateVote(pollId, userId, anonymousId);
      if (!validation.canVote) {
        return { success: false, error: validation.reason || 'Cannot vote on this poll' };
      }

      let voteId: string | null = null;
      let pointsAwarded = 0;

      // Submit vote based on user type
      if (userId) {
        const { data: voteData, error: voteError } = await supabase
          .from('poll_votes')
          .insert({
            poll_id: pollId,
            user_id: userId,
            option_id: optionId
          })
          .select('id')
          .single();

        if (voteError) {
          debugVoteFlow('VOTING_SERVICE_FALLBACK_VOTE_ERROR', { error: voteError });
          if (voteError.code === '23505') {
            return { success: false, error: 'You have already voted on this poll' };
          }
          return { success: false, error: 'Failed to record vote' };
        }

        voteId = voteData?.id;

        // Award points
        try {
          let countryCode = poll?.country_code;
          if (!countryCode) {
            countryCode = await PointsService.getPollCountryCode(pollId);
          }

          if (countryCode) {
            const pointsEnabled = await PointsService.isPollPointsEnabled(pollId);
            
            if (pointsEnabled) {
              const pointsResult = await PointsService.awardPollPoints(userId, pollId, countryCode);
              if (pointsResult.success && pointsResult.points) {
                pointsAwarded = pointsResult.points;
              }
            }
          }
        } catch (pointsError) {
          console.warn('Points processing failed but vote recorded:', pointsError);
        }

      } else if (anonymousId) {
        const { data: anonymousVoteData, error: anonymousVoteError } = await supabase
          .from('anonymous_poll_votes')
          .insert({
            poll_id: pollId,
            option_id: optionId,
            anonymous_id: anonymousId
          })
          .select('id')
          .single();

        if (anonymousVoteError) {
          debugVoteFlow('VOTING_SERVICE_FALLBACK_ANONYMOUS_ERROR', { error: anonymousVoteError });
          if (anonymousVoteError.code === '23505') {
            return { success: false, error: 'You have already voted on this poll' };
          }
          return { success: false, error: 'Failed to record anonymous vote' };
        }

        voteId = anonymousVoteData?.id;
      } else {
        return { success: false, error: 'No user identity provided' };
      }

      // Update poll statistics
      try {
        await supabase.rpc('update_poll_options_counts', { poll_id: pollId });
      } catch (updateError) {
        console.warn('Failed to update poll counts:', updateError);
      }

      return { 
        success: true, 
        voteId: voteId || undefined,
        pointsAwarded: pointsAwarded > 0 ? pointsAwarded : undefined,
        selectedOptionId: optionId // Track the selected option
      };

    } catch (error) {
      debugVoteFlow('VOTING_SERVICE_FALLBACK_ERROR', { error });
      return { success: false, error: 'System error during vote submission' };
    }
  }

  /**
   * Get user's existing vote for a poll
   */
  static async getUserVote(pollId: string, userId?: string, anonymousId?: string): Promise<number | null> {
    try {
      if (userId) {
        const { data: vote } = await supabase
          .from('poll_votes')
          .select('option_id')
          .eq('poll_id', pollId)
          .eq('user_id', userId)
          .maybeSingle();

        return vote?.option_id || null;
      }

      if (anonymousId) {
        const { data: vote } = await supabase
          .from('anonymous_poll_votes')
          .select('option_id')
          .eq('poll_id', pollId)
          .eq('anonymous_id', anonymousId)
          .maybeSingle();

        return vote?.option_id || null;
      }

      return null;
    } catch (error) {
      console.error('Exception getting user vote:', error);
      return null;
    }
  }
}
